export type ModelSection = 'sweetspot' | 'models'

export interface ModelOption {
  value: string
  readableValue: string
  label: string
  description?: string
  /** Sweetspot's own ways to run a chat, or a specific model. */
  section?: ModelSection
  /** How much reasoning a Sweetspot pick spends, drawn as filled bars. */
  level?: ModelLevel
}

export type ModelLevel = 1 | 2 | 3

export type ModelVendor = 'anthropic' | 'openai' | 'google'

export const MODEL_SECTION_LABELS: Record<ModelSection, string> = {
  sweetspot: 'Sweetspot',
  models: 'More',
}

export const LITE_MODEL_SELECTOR: ModelOption = {
  value: 'sweetspot/v1/model/gemini-3.8-flash~medium',
  readableValue: 'Lite',
  label: 'Lite',
  description: 'Quick answers for simple questions and lookups.',
  section: 'sweetspot',
  level: 1,
}

export const AUTO_MODEL_SELECTOR: ModelOption = {
  value: 'sweetspot/v1/auto',
  readableValue: 'Normal',
  label: 'Normal',
  description: 'Sweetspot picks the best model for everyday work.',
  section: 'sweetspot',
  level: 2,
}

export const ULTRA_MODEL_SELECTOR: ModelOption = {
  value: 'sweetspot/v1/model/gpt-6-sol~high',
  readableValue: 'Ultra',
  label: 'Ultra',
  description: 'For hard analysis and complex drafting.',
  section: 'sweetspot',
  level: 3,
}

export const OPUS_MODEL_SELECTOR: ModelOption = {
  value: 'sweetspot/v1/model/claude-opus-5-5',
  readableValue: 'Opus 5.5',
  label: 'Opus 5.5',
  description: 'Strong writer and careful reasoner.',
  section: 'models',
}

export const GPT_MODEL_SELECTOR: ModelOption = {
  value: 'sweetspot/v1/model/gpt-6-sol',
  readableValue: 'GPT-6 Sol',
  label: 'GPT-6 Sol',
  description: 'Broad knowledge and precise tool use.',
  section: 'models',
}

export const GEMINI_MODEL_SELECTOR: ModelOption = {
  value: 'sweetspot/v1/model/gemini-3.8-flash',
  readableValue: 'Gemini 3.8 Flash',
  label: 'Gemini 3.8 Flash',
  description: 'Fast, with a very large context window.',
  section: 'models',
}

export const MODEL_SELECTOR_OPTIONS: ModelOption[] = [
  LITE_MODEL_SELECTOR,
  AUTO_MODEL_SELECTOR,
  ULTRA_MODEL_SELECTOR,
  OPUS_MODEL_SELECTOR,
  GPT_MODEL_SELECTOR,
  GEMINI_MODEL_SELECTOR,
]

/** Each vendor's model in the picker, where an older vendor-level pick lands. */
export const VENDOR_MODEL_SELECTORS: Record<ModelVendor, ModelOption> = {
  anthropic: OPUS_MODEL_SELECTOR,
  openai: GPT_MODEL_SELECTOR,
  google: GEMINI_MODEL_SELECTOR,
}

/** The vendor a pick runs on, by its model name or vendor lane; null for Sweetspot's routing. */
export const vendorOfOption = (value: string): ModelVendor | null => {
  const name = value.startsWith('sweetspot/')
    ? /^sweetspot\/v1\/(?:model|vendor)\/(.+)$/.exec(value)?.[1]
    : value
  if (!name) return null
  // Older stored forms carry a provider or gateway prefix: `gateway/google-cloud:gemini-…`.
  if (/(?:^|[/:])(?:anthropic|claude)/i.test(name)) return 'anthropic'
  if (/(?:^|[/:])(?:openai|gpt|o1|o3)/i.test(name)) return 'openai'
  if (/(?:^|[/:])(?:google|gemini)/i.test(name)) return 'google'
  return null
}
